import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import {
  IInvoiceServiceAgyLogData,
  IInvoiceServiceAgyLogItem,
} from "../../../../models/invoice-service-agy-log";

const INVOICE_SERVICE_AGY_LOG_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/InvoiceServiceAgyLog/InvoiceServiceAgysLogs";

interface IInvoiceServiceAgyDetailsModalProps {
  invoiceServiceAgyData: IInvoiceServiceAgyLogData;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.invoiceServiceAgy.details.";

const InvoiceServiceAgyDetailsModal: React.FC<
  IInvoiceServiceAgyDetailsModalProps
> = ({ invoiceServiceAgyData, onClose }) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<IInvoiceServiceAgyLogItem[]>([]);
  const [selectedLogData, setSelectedLogData] = useState<string>("");
  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GlobalAxios.get(INVOICE_SERVICE_AGY_LOG_ROUTE, {
          params: {
            InvoiceServiceAgyIdentifier: invoiceServiceAgyData.identifier,
            ExtensionCode: "72",
          },
        });

        if (response.data?.items) {
          setLogData(
            response.data.items.map(
              (item: IInvoiceServiceAgyLogItem, index: number) => ({
                ...item,
                id: item.identifier ?? index + 1,
              }),
            ),
          );
        }
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
      }
    };

    fetchData();
  }, [invoiceServiceAgyData.identifier]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const columns: GridColDef<IInvoiceServiceAgyLogItem>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "log_id") || "ID do Log",
      width: 100,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "statusItem",
      headerName: t(translationPath + "status") || "Status",
      width: 180,
      renderCell: (params) => {
        const rawStatus =
          (params.row.statusItem || (params.row.success ? "SUCESSO" : "ERRO"))
            ?.toString()
            .toUpperCase() || "";

        const getConfig = (status: string) => {
          const map: Record<
            string,
            { color: "warning" | "info" | "success" | "error"; key: string }
          > = {
            "COM INCONSISTENCIA": {
              color: "warning",
              key: "with_inconsistency",
            },
            "AGUARDANDO ENVIO": { color: "info", key: "awaiting_send" },
            ENVIADO: { color: "success", key: "sent" },
            ERRO: { color: "error", key: "error" },
            SUCESSO: { color: "success", key: "success" },
          };

          return map[status] || { color: "warning" as const, key: "awaiting_send" };
        };

        const config = getConfig(rawStatus);

        return (
          <Chip
            label={t(`page.4uBus.logs.invoiceServiceAgy.status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "logData",
      headerName: t(translationPath + "log_data") || "Dados do Log",
      flex: 1,
      minWidth: 320,
      renderCell: (params) => {
        const rowLogData = (params.value || "") as string;
        const preview =
          rowLogData.length > 150
            ? `${rowLogData.substring(0, 150)}...`
            : rowLogData;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview || "-"}
            </Typography>
            {rowLogData && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedLogData(rowLogData);
                    setShowLogModal(true);
                  }}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes do Invoice Service Agy"} -{" "}
          {invoiceServiceAgyData.identifier}
        </DialogTitle>
        <DialogContent
          sx={{ height: 500, width: "100%", marginTop: 0, overflow: "hidden" }}
        >
          <TableList
            columns={columns}
            rows={logData}
            fileName={`invoice-service-agy-logs-${invoiceServiceAgyData.identifier}`}
            showSelects={false}
            onRowSelectionModelChange={undefined}
            pageSizeOptions={[5, 10, 25, 50]}
            hideTitle={true}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={showLogModal}
        onClose={() => setShowLogModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t(translationPath + "log_data") || "Dados do Log"}</DialogTitle>
        <DialogContent>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: (theme) => theme.palette.grey[100],
              padding: 2,
              borderRadius: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {selectedLogData}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogModal(false)}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceServiceAgyDetailsModal;

